/* global google */
import React, { useEffect, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "650px",
};

// Convert key to Title Case
const formatKey = (key) =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Get map center from GeoJSON
function getGeoJSONCenter(geojson) {
  let latSum = 0;
  let lngSum = 0;
  let count = 0;

  geojson.features.forEach((feature) => {
    const coords = feature.geometry.coordinates.flat(2);
    for (let i = 0; i < coords.length; i += 2) {
      lngSum += coords[i];
      latSum += coords[i + 1];
      count++;
    }
  });

  return count > 0
    ? { lat: latSum / count, lng: lngSum / count }
    : { lat: 0, lng: 0 };
}

// Get color based on numeric value
const getColor = (value, min, max) => {
  if (value === undefined) return "gray";
  const ratio = (value - min) / (max - min || 1);
  const r = Math.floor(255 * ratio);
  const g = Math.floor(255 * (1 - ratio));
  return `rgb(${r},${g},0)`; // Green → Yellow → Red
};

export default function GeojsonDataLayerMap({ geojsonData }) {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const infowindowRef = useRef(null);

  const concentrations = geojsonData.features
    .map((f) => f.properties.concentration)
    .filter((v) => v !== undefined);
  const intensities = geojsonData.features
    .map((f) => f.properties.intensity)
    .filter((v) => v !== undefined);

  const minConcentration = concentrations.length
    ? Math.min(...concentrations)
    : 0;
  const maxConcentration = concentrations.length
    ? Math.max(...concentrations)
    : 1;
  const minIntensity = intensities.length ? Math.min(...intensities) : 0;
  const maxIntensity = intensities.length ? Math.max(...intensities) : 1;

  useEffect(() => {
    if (mapLoaded && mapRef.current && window.google) {
      const dataLayer = new window.google.maps.Data({ map: mapRef.current });
      dataLayer.addGeoJson(geojsonData);

      // Style features
      dataLayer.setStyle((feature) => {
        const concentration = feature.getProperty("concentration");
        const intensity = feature.getProperty("intensity");

        const fillColor =
          concentration !== undefined
            ? getColor(concentration, minConcentration, maxConcentration)
            : intensity !== undefined
            ? getColor(intensity, minIntensity, maxIntensity)
            : "#ccc";

        return {
          fillColor,
          strokeColor: "black",
          strokeWeight: 1,
          fillOpacity: 0.6,
        };
      });

      //Auto-zoom to fit all features
      const bounds = new window.google.maps.LatLngBounds();
      dataLayer.forEach((feature) => {
        const geometry = feature.getGeometry();
        geometry.forEachLatLng((latLng) => {
          bounds.extend(latLng);
        });
      });
      if (!bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, 50); // 50px padding
        const listener = google.maps.event.addListener(
          mapRef.current,
          "idle",
          function () {
            if (mapRef.current.getZoom() > 17) mapRef.current.setZoom(17); // max zoom
            google.maps.event.removeListener(listener);
          }
        );
      }

      //Mouse over: highlight + show InfoWindow
      dataLayer.addListener("mouseover", (e) => {
        dataLayer.overrideStyle(e.feature, {
          strokeWeight: 3,
          fillOpacity: 0.8,
        });

        const properties = {};
        e.feature.forEachProperty((value, key) => {
          properties[key] = value;
        });

        const tableContent = `
        <table style="border-collapse: collapse; min-width: 150px;">
          ${Object.entries(properties)
            .map(([key, val]) =>
              key.toLowerCase().includes("id")
                ? null
                : `<tr>
                   <td style="border: 1px solid #ccc; padding: 4px; font-weight: bold;">${formatKey(
                     key
                   )}</td>
                   <td style="border: 1px solid #ccc; padding: 4px;">${val}</td>
                 </tr>`
            )
            .join("")}
        </table>
      `;

        if (!infowindowRef.current)
          infowindowRef.current = new window.google.maps.InfoWindow();
        infowindowRef.current.setContent(tableContent);
        infowindowRef.current.setPosition(e.latLng);
        infowindowRef.current.open(mapRef.current);
      });

      //Mouse out: reset style + close InfoWindow
      dataLayer.addListener("mouseout", (e) => {
        dataLayer.revertStyle(e.feature);

        if (infowindowRef.current) {
          infowindowRef.current.close();
        }
      });
    }
  }, [mapLoaded, geojsonData]);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={getGeoJSONCenter(geojsonData)}
      zoom={16}
      onLoad={(map) => {
        mapRef.current = map;
        setMapLoaded(true);
      }}
      options={{
        mapTypeId: "roadmap",
        disableDefaultUI: true,
        zoomControl: true,
        myLocationControl: true,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          position: google.maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: ["roadmap", "satellite"],
        },
      }}
    />
  );
}
